import { PATCH_LOG_LOCALES, type PatchLogEntryPublic, type PatchLogPublic } from './types';

/**
 * `version_label` has no unique constraint, so one version can be published as
 * several logs — a re-release, a follow-up publish, an import run twice. The
 * reader does not care which row a change came from; they care that "Versi
 * 2.14" appears once. Everything here is display-only: acknowledgments and
 * deferrals are keyed on the underlying log ids, so callers must keep using the
 * raw list for those.
 */

/**
 * Grouping key for a version, matching what the user actually reads:
 * `formatPatchLogVersionLabel` drops a leading "v", so "v2.14" and "2.14"
 * render the same line and have to land in the same group. A blank label is no
 * version at all and so cannot be a duplicate of anything — it gets a key of
 * its own, on a prefix no real label can reach.
 */
const versionKey = (log: PatchLogPublic): string => {
  const trimmed = (log.versionLabel ?? '').trim().replace(/\s+/g, ' ');
  // Only a "v" glued to a digit is dropped, exactly as the formatter drops it —
  // "versi 2.14" keeps its "v" on both sides of the comparison.
  return trimmed === '' ? `blank:${log.id}` : `version:${trimmed.replace(/^v(?=\d)/i, '').toLowerCase()}`;
};

/**
 * Identity by content, not by row id: merged logs are separate rows, so the
 * same change republished under one version carries a fresh entry id every
 * time. Locales are read in a fixed order and the whole thing goes through
 * JSON, so no separator can be forged out of the text itself. Images are left
 * out — two entries that read identically are the same news to the user
 * whichever screenshots hang off them.
 */
const entrySignature = (entry: PatchLogEntryPublic): string =>
  JSON.stringify([
    entry.category,
    PATCH_LOG_LOCALES.map((code) => entry.title?.[code] ?? ''),
    PATCH_LOG_LOCALES.map((code) => entry.area?.[code] ?? ''),
    PATCH_LOG_LOCALES.map((code) => entry.points?.[code] ?? []),
  ]);

/**
 * Collapses logs that share a version into one, preserving the incoming order
 * (callers sort newest first). The first log of a group keeps its identity —
 * id, version label, release date, published date — and absorbs the entries of
 * the later ones, minus any that repeat a change already listed. A group of one
 * is returned untouched, so the common case allocates nothing new.
 */
export function collapsePatchLogsByVersion(logs: readonly PatchLogPublic[]): PatchLogPublic[] {
  const order: string[] = [];
  const groups = new Map<
    string,
    { head: PatchLogPublic; count: number; entries: PatchLogEntryPublic[]; seen: Set<string> }
  >();

  for (const log of logs) {
    const key = versionKey(log);
    let group = groups.get(key);
    if (!group) {
      group = { head: log, count: 0, entries: [], seen: new Set<string>() };
      groups.set(key, group);
      order.push(key);
    }
    group.count += 1;
    for (const entry of log.entries ?? []) {
      const signature = entrySignature(entry);
      if (group.seen.has(signature)) {
        continue;
      }
      group.seen.add(signature);
      group.entries.push(entry);
    }
  }

  const collapsed: PatchLogPublic[] = [];
  for (const key of order) {
    const group = groups.get(key);
    if (!group) {
      continue;
    }
    collapsed.push(group.count === 1 ? group.head : { ...group.head, entries: group.entries });
  }
  return collapsed;
}
